import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Company } from "src/api/client-api/useUserNew";
import { CompaniesService } from "src/services/Companies/CompaniesService";
import { companySlug } from "src/services/URLHandler";

type ResolveSelectedCompanyIdArgs = {
    urlCompanyId?: number;
    queryCompanyId?: number;
    storedCompanyId: number | null;
    firstCompanyId?: number;
    isQueryCompanyIdValid: boolean;
    isStoredCompanyIdValid: boolean;
    isUrlCompanyIdValid: boolean;
    shouldPreferQueryCompanyId: boolean;
};

export const resolveSelectedCompanyId = ({
    urlCompanyId,
    queryCompanyId,
    storedCompanyId,
    firstCompanyId,
    isQueryCompanyIdValid,
    isStoredCompanyIdValid,
    isUrlCompanyIdValid,
    shouldPreferQueryCompanyId,
}: ResolveSelectedCompanyIdArgs): number | undefined => {
    if (shouldPreferQueryCompanyId && isQueryCompanyIdValid) {
        return queryCompanyId;
    }

    if (
        urlCompanyId !== undefined &&
        !Number.isNaN(urlCompanyId) &&
        (shouldPreferQueryCompanyId || isUrlCompanyIdValid)
    ) {
        return urlCompanyId;
    }

    if (isQueryCompanyIdValid) {
        return queryCompanyId;
    }

    // PDF/CMS renders must not substitute a different company when the requested one is unavailable.
    if (shouldPreferQueryCompanyId) {
        return undefined;
    }

    if (isStoredCompanyIdValid) {
        return storedCompanyId ?? undefined;
    }

    return firstCompanyId;
};

export const useSelectCompany = (allCompanies: Company[] | null) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [selectedCompanyState, setSelectedCompany] = useState<Company | null>(null);
    const routePrefix = `/${companySlug}/`;
    const routeCompanySegment =
        location.pathname.startsWith(routePrefix) ?
            location.pathname.slice(routePrefix.length).split("/")[0]
        :   undefined;
    const hasCompanyRoute = routeCompanySegment !== undefined;
    const urlCompanyId =
        routeCompanySegment !== undefined && routeCompanySegment.trim() !== "" ?
            Number(routeCompanySegment)
        :   undefined;
    const params = new URLSearchParams(location.search);
    const queryCompanyIdRaw = params.get("selected_organization_id") ?? params.get("selected_company_id");
    const shouldPreferQueryCompanyId = params.get("__pdf") === "1" || params.get("__force_cms") === "1";
    const selectionKey = JSON.stringify([routeCompanySegment, queryCompanyIdRaw, shouldPreferQueryCompanyId]);
    const [selectedCompanyKey, setSelectedCompanyKey] = useState(selectionKey);
    const selectedCompany =
        selectedCompanyKey === selectionKey ?
            (allCompanies?.find((company) => company.id === selectedCompanyState?.id) ?? null)
        :   null;

    useEffect(() => {
        if (allCompanies === null) return;

        const queryCompanyId =
            queryCompanyIdRaw !== null && queryCompanyIdRaw.trim() !== "" ? Number(queryCompanyIdRaw) : undefined;
        const hasValidQueryCompanyId =
            queryCompanyId !== undefined &&
            Number.isFinite(queryCompanyId) &&
            allCompanies.some((company) => company.id === queryCompanyId);

        const storedCompanyId = CompaniesService.getCurrentCompanyId();
        const isStoredCompanyValid =
            storedCompanyId !== null && allCompanies.some((company) => company.id === storedCompanyId);
        const isUrlCompanyIdValid =
            urlCompanyId !== undefined &&
            Number.isFinite(urlCompanyId) &&
            allCompanies.some((company) => company.id === urlCompanyId);
        const firstCompanyId = allCompanies.length > 0 ? allCompanies[0].id : undefined;
        const selectedCompanyId = resolveSelectedCompanyId({
            urlCompanyId,
            queryCompanyId,
            storedCompanyId,
            firstCompanyId,
            isQueryCompanyIdValid: hasValidQueryCompanyId,
            isStoredCompanyIdValid: isStoredCompanyValid,
            isUrlCompanyIdValid,
            shouldPreferQueryCompanyId,
        });

        const nextSelectedCompany =
            selectedCompanyId === undefined ? null : (
                CompaniesService.selectCurrentCompany(allCompanies, selectedCompanyId)
            );
        setSelectedCompany(nextSelectedCompany);

        if (
            !shouldPreferQueryCompanyId &&
            hasCompanyRoute &&
            selectedCompanyId !== undefined &&
            selectedCompanyId !== urlCompanyId
        ) {
            const nextPathSegments = location.pathname.split("/");
            nextPathSegments[2] = String(selectedCompanyId);
            navigate(
                { pathname: nextPathSegments.join("/"), search: location.search, hash: location.hash },
                { replace: true },
            );
        }
        setSelectedCompanyKey(selectionKey);
    }, [
        allCompanies,
        hasCompanyRoute,
        location.hash,
        location.pathname,
        location.search,
        navigate,
        selectionKey,
        urlCompanyId,
    ]);

    return {
        selectedCompany,
        setSelectedCompany,
    };
};
